import { useCallback, useEffect, useState } from "react";
import { Trophy, Timer, Flame, UtensilsCrossed, Lock, RefreshCw } from "lucide-react";
import { api, money, errorMessage } from "../api";
import "./insights.css";
import { DateInput } from "../components/DateInput";

const istToday = (offset = 0) => {
  const d = new Date(Date.now() + 330 * 60000 - offset * 86400000);
  return d.toISOString().slice(0, 10);
};
const PRESETS = [
  { id: "today", label: "Today", r: () => [istToday(), istToday()] },
  { id: "yesterday", label: "Yesterday", r: () => [istToday(1), istToday(1)] },
  { id: "7d", label: "Last 7 days", r: () => [istToday(6), istToday()] },
  { id: "30d", label: "Last 30 days", r: () => [istToday(29), istToday()] },
];
const ddmmyyyy = (iso) => (iso ? iso.split("-").reverse().join("-") : "");
const hourLabel = (h) => `${((h + 11) % 12) + 1} ${h < 12 ? "AM" : "PM"}`;
const mins = (m) => (m == null ? "—" : m >= 60 ? `${Math.floor(m / 60)}h ${Math.round(m % 60)}m` : `${Math.round(m)} min`);

function Highlight({ icon: Icon, label, value, sub, testid, tone }) {
  return (
    <div className={`ins-card ${tone || ""}`} data-testid={testid}>
      <span className="ins-ico"><Icon size={16} /></span>
      <span className="overline">{label}</span>
      <strong>{value}</strong>
      <small>{sub}</small>
    </div>
  );
}

function HourChart({ hours, peak }) {
  const max = Math.max(1, ...hours.map((h) => h.sales));
  return (
    <div className="ins-hours" data-testid="insights-hour-chart">
      {hours.map((h) => (
        <div key={h.hour} className={peak && h.hour === peak.hour && h.sales > 0 ? "peak" : ""} title={`${hourLabel(h.hour)} · ${money(h.sales)} · ${h.bills} bills`}>
          <i style={{ height: `${(h.sales / max) * 100}%` }} />
          <small>{h.hour % 3 === 0 ? hourLabel(h.hour) : ""}</small>
        </div>
      ))}
    </div>
  );
}

function WaiterTable({ rows, top }) {
  return (
    <div className="table-wrap"><table data-testid="insights-waiter-table">
      <thead><tr><th>#</th><th>Waiter</th><th>Tables served</th><th>Items</th><th>Sales</th><th>Avg bill</th><th>Avg service</th><th>Avg table time</th></tr></thead>
      <tbody>
        {!rows.length && <tr><td colSpan={8}><div className="empty small">No dine-in bills in this range.</div></td></tr>}
        {rows.map((w, i) => (
          <tr key={w.waiter_id || "none"} className={top && w.waiter_id === top.waiter_id ? "ins-top-row" : ""} data-testid={`insights-waiter-row-${i}`}>
            <td>{i + 1}</td><td><b>{w.name}</b></td><td>{w.tables}</td><td>{w.items}</td><td>{money(w.sales)}</td><td>{money(w.avg_bill)}</td><td>{mins(w.avg_service_mins)}</td><td>{mins(w.avg_turnover_mins)}</td>
          </tr>
        ))}
      </tbody>
    </table></div>
  );
}

function TopItems({ qty, revenue }) {
  const [by, setBy] = useState("qty");
  const list = by === "qty" ? qty : revenue;
  const max = Math.max(1, ...list.map((i) => (by === "qty" ? i.qty : i.revenue)));
  return (
    <div className="panel" data-testid="insights-top-items">
      <div className="panel-head"><div><span className="overline">TOP SELLING ITEMS</span><h2>All channels</h2></div>
        <div className="ins-toggle">
          <button className={by === "qty" ? "on" : ""} onClick={() => setBy("qty")} data-testid="insights-items-by-qty">By qty</button>
          <button className={by === "revenue" ? "on" : ""} onClick={() => setBy("revenue")} data-testid="insights-items-by-revenue">By revenue</button>
        </div>
      </div>
      {!list.length && <div className="empty small">No sales in this range.</div>}
      {list.map((it, i) => (
        <div className="ins-item" key={it.name} data-testid={`insights-item-${i}`}>
          <span>{i + 1}</span><b>{it.name}</b>
          <div className="ins-meter"><i style={{ width: `${((by === "qty" ? it.qty : it.revenue) / max) * 100}%` }} /></div>
          <em>{by === "qty" ? `${it.qty} sold` : money(it.revenue)}</em>
        </div>
      ))}
    </div>
  );
}

function TableSpeed({ rows }) {
  return (
    <div className="panel full-panel" data-testid="insights-table-speed">
      <div className="panel-head" style={{ padding: "20px 24px 0" }}><div><span className="overline">TABLE SERVICE SPEED</span><h2>Fastest first · KOT → food ready, and seated → paid</h2></div></div>
      <div className="table-wrap"><table>
        <thead><tr><th>Table</th><th>Bills</th><th>Avg service</th><th>Avg table time</th><th>Sales</th></tr></thead>
        <tbody>
          {!rows.length && <tr><td colSpan={5}><div className="empty small">No dine-in bills in this range.</div></td></tr>}
          {rows.map((t, i) => (
            <tr key={t.table + i} data-testid={`insights-table-row-${i}`}><td><b>{t.table}</b></td><td>{t.tabs}</td><td>{mins(t.avg_service_mins)}</td><td>{mins(t.avg_turnover_mins)}</td><td>{money(t.sales)}</td></tr>
          ))}
        </tbody>
      </table></div>
    </div>
  );
}

export default function InsightsView({ branch, allowed, onUpgrade }) {
  const [preset, setPreset] = useState("today");
  const [range, setRange] = useState(PRESETS[0].r());
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    if (!allowed) return;
    try { setData((await api.get(`/insights?branch=${branch}&date_from=${range[0]}&date_to=${range[1]}`)).data); setErr(""); }
    catch (e) { setErr(errorMessage(e)); }
  }, [branch, range, allowed]);
  useEffect(() => { load(); }, [load]);

  if (!allowed) {
    return (
      <div className="panel kds-locked" data-testid="insights-locked">
        <Lock size={22} /><h2>Performance Insights is a Professional feature</h2>
        <p>Waiter leaderboard, peak hours, fastest tables and best-sellers for any date range.</p>
        <button className="primary-button" onClick={onUpgrade} data-testid="insights-upgrade-btn">See plans</button>
      </div>
    );
  }
  const pick = (p) => { setPreset(p.id); setRange(p.r()); };
  const setCustom = (i, v) => { setPreset("custom"); setRange((r) => (i === 0 ? [v, r[1] < v ? v : r[1]] : [r[0] > v ? v : r[0], v])); };
  const tw = data?.top_waiter, ft = data?.fastest_table, ph = data?.peak_hour, ti = data?.top_items_qty?.[0];

  return (
    <div data-testid="insights-page">
      <div className="ins-bar">
        <div className="ins-toggle">
          {PRESETS.map((p) => <button key={p.id} className={preset === p.id ? "on" : ""} onClick={() => pick(p)} data-testid={`insights-preset-${p.id}`}>{p.label}</button>)}
        </div>
        <label>From<DateInput lang="en-GB" value={range[0]} onChange={(e) => e.target.value && setCustom(0, e.target.value)} data-testid="insights-from" /></label>
        <label>To<DateInput lang="en-GB" value={range[1]} onChange={(e) => e.target.value && setCustom(1, e.target.value)} data-testid="insights-to" /></label>
        <span className="filter-spacer" />
        <span className="muted-label" data-testid="insights-range-label">{data ? `${ddmmyyyy(data.range.from)} → ${ddmmyyyy(data.range.to)} · IST` : ""}</span>
        <button className="icon-button" onClick={load} data-testid="insights-refresh"><RefreshCw size={14} /></button>
      </div>
      {err && <div className="auth-alert error" data-testid="insights-error">{err}</div>}
      {data && (
        <>
          <div className="ins-totals" data-testid="insights-totals">
            <div><span>Sales</span><b data-testid="insights-total-sales">{money(data.totals.sales)}</b></div>
            <div><span>Bills</span><b data-testid="insights-total-bills">{data.totals.bills}</b></div>
            <div><span>Avg bill</span><b>{money(data.totals.avg_bill)}</b></div>
            <div><span>Dine-in tables</span><b>{data.totals.dine_in_tabs}</b></div>
          </div>
          <div className="ins-highlights">
            <Highlight icon={Trophy} tone="gold" label="TOP WAITER · BY SALES" testid="insights-top-waiter" value={tw ? tw.name : "—"} sub={tw ? `${money(tw.sales)} · ${tw.tables} tables` : "No assigned waiter bills"} />
            <Highlight icon={Timer} tone="green" label="FASTEST TABLE SERVICE" testid="insights-fastest-table" value={ft ? ft.table : "—"} sub={ft ? `${mins(ft.avg_service_mins)} KOT→ready · ${mins(ft.avg_turnover_mins)} seated→paid` : "No dine-in bills"} />
            <Highlight icon={Flame} tone="red" label="PEAK HOUR" testid="insights-peak-hour" value={ph ? `${hourLabel(ph.hour)} – ${hourLabel((ph.hour + 1) % 24)}` : "—"} sub={ph ? `${money(ph.sales)} · ${ph.bills} bills` : "No sales"} />
            <Highlight icon={UtensilsCrossed} label="TOP SELLING ITEM" testid="insights-top-item" value={ti ? ti.name : "—"} sub={ti ? `${ti.qty} sold · ${money(ti.revenue)}` : "No sales"} />
          </div>
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-head"><div><span className="overline">PEAK HOURS · ALL CHANNELS</span><h2>Sales by hour (IST)</h2></div></div>
            <HourChart hours={data.hourly} peak={ph} />
          </div>
          <div className="panel full-panel" style={{ marginBottom: 16 }}>
            <div className="panel-head" style={{ padding: "20px 24px 0" }}><div><span className="overline">WAITER PERFORMANCE · DINE-IN</span><h2>Leaderboard ranked by sales</h2></div></div>
            <WaiterTable rows={data.waiters} top={tw} />
          </div>
          <div className="panel full-panel" style={{ marginBottom: 16 }} data-testid="insights-top-customers">
            <div className="panel-head" style={{ padding: "20px 24px 0" }}><div><span className="overline">TOP RETURNING CUSTOMERS · BY VISITS</span><h2>Loyal regulars in this range</h2></div></div>
            <div className="table-wrap"><table>
              <thead><tr><th>#</th><th>Customer</th><th>Mobile</th><th>Visits</th><th>Spent</th></tr></thead>
              <tbody>
                {!data.top_customers?.length && <tr><td colSpan={5}><div className="empty small">No bills with customer mobile numbers in this range.</div></td></tr>}
                {(data.top_customers || []).map((c, i) => <tr key={c.phone} data-testid={`insights-customer-row-${i}`}><td>{i + 1}</td><td><b>{c.name}</b></td><td>{c.phone}</td><td>{c.visits}</td><td>{money(c.spent)}</td></tr>)}
              </tbody>
            </table></div>
          </div>
          <div className="ins-split">
            <TopItems qty={data.top_items_qty} revenue={data.top_items_revenue} />
            <TableSpeed rows={data.tables} />
          </div>
        </>
      )}
    </div>
  );
}
